import type { PaginatedResponse } from "./courseCertificates";
import { cleApi, getCleApiPrefix } from "./client";
import { openReportWithAuth } from "../util";
import type {
  DocumentReclassifyStatus,
  ReclassifyDocumentResponse,
} from "./models/documents";
import type { CleFieldValidationMap } from "./models/fieldValidation";
import type { ReviewThreadListItemModel } from "./models/reviewThreads";
import axios from "axios";

export interface InsurancePolicyModel {
  id: string;
  fileName: string | null;
  documentType: string;
  ownerUserId: string;
  createdByUserId: string;
  createdByUserName: string | null;
  createdAt: string;
  updatedAt: string;
  status: string;
  aiDecision: string | null;
  aiRejectionReason: string | null;
  insuranceCompany: string | null;
  policyStartDate: string | null;
  lawyerLiabilityAmount: number | null;
  guaranteeAmount: number | null;
  fieldValidation: CleFieldValidationMap | null;
  reviewThread: ReviewThreadListItemModel | null;
}

export interface UpdateInsurancePolicyRequest {
  insurance_company?: string | null;
  policy_start_date?: string | null;
  lawyer_liability_amount?: number | null;
  guarantee_amount?: number | null;
}

export interface UpdateInsurancePolicyParams {
  userId: string;
  id: string;
  body: UpdateInsurancePolicyRequest;
}

export interface ReclassifyInsurancePolicyParams {
  userId: string;
  id: string;
  newStatus: DocumentReclassifyStatus;
}

export interface GetInsurancePoliciesParams {
  userId: string;
  page?: number;
  pageSize?: number;
}

export interface GetInsurancePolicyByIdParams {
  userId: string;
  insurancePolicyId: string;
}

export const getInsurancePolicies = ({
  userId,
  page = 1,
  pageSize = 20,
}: GetInsurancePoliciesParams): Promise<
  PaginatedResponse<InsurancePolicyModel>
> => {
  const query = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
  });

  return cleApi.get<PaginatedResponse<InsurancePolicyModel>>(
    `users/${encodeURIComponent(userId)}/insurance-policies?${query.toString()}`,
  );
};

export const getInsurancePolicyById = ({
  userId,
  insurancePolicyId,
}: GetInsurancePolicyByIdParams): Promise<InsurancePolicyModel> =>
  cleApi.get<InsurancePolicyModel>(
    `users/${encodeURIComponent(userId)}/insurance-policies/${encodeURIComponent(
      insurancePolicyId,
    )}`,
  );

export const updateInsurancePolicy = ({
  userId,
  id,
  body,
}: UpdateInsurancePolicyParams): Promise<InsurancePolicyModel> =>
  cleApi.put<InsurancePolicyModel>(
    `users/${encodeURIComponent(userId)}/insurance-policies/${encodeURIComponent(id)}`,
    body,
  );

export const reclassifyInsurancePolicy = ({
  userId,
  id,
  newStatus,
}: ReclassifyInsurancePolicyParams): Promise<ReclassifyDocumentResponse> =>
  cleApi.post<ReclassifyDocumentResponse>(
    `users/${encodeURIComponent(userId)}/insurance-policies/${encodeURIComponent(id)}/reclassify`,
    { new_status: newStatus },
  );

export const openInsurancePolicyDownload = ({
  userId,
  insurancePolicyId,
}: GetInsurancePolicyByIdParams): Promise<void> =>
  openReportWithAuth(
    `${getCleApiPrefix()}users/${encodeURIComponent(
      userId,
    )}/insurance-policies/${encodeURIComponent(insurancePolicyId)}/download`,
  );

export const getInsurancePolicyPdfBlob = async ({
  userId,
  insurancePolicyId,
}: GetInsurancePolicyByIdParams): Promise<Blob> => {
  const response = await axios.get<Blob>(
    `${getCleApiPrefix()}users/${encodeURIComponent(
      userId,
    )}/insurance-policies/${encodeURIComponent(insurancePolicyId)}/download`,
    { responseType: "blob" },
  );

  return response.data;
};
